import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
    Avatar,
    AvatarGradientFallback,
    cn,
    initialsFor,
    ListCard,
    ListCardRow,
    Mono,
    Skeleton,
    Text,
} from "@athena/ui";
import { alumnusPath, fullName } from "../helpers/client";
import { useOpenedFromHere } from "../helpers/openedFrom";
import {
    daysUntil,
    formatMilestoneDay,
    milestoneWhen,
    type Milestone,
    type MilestoneWhen,
} from "../helpers/milestones";

/** Placeholder rows while the clients load. */
const SKELETON_ROWS = 3;

/** A today row's tint, matching its badge's color. */
const TODAY_TINT = {
    brand: "bg-secondary/10 hover:bg-secondary/15",
    success: "bg-success/10 hover:bg-success/15",
} as const;

interface MilestoneCardProps<M extends Milestone> {
    title: string;
    milestones: M[];
    loading: boolean;
    /** Shown in place of rows: nothing this month, or the load failed. */
    empty: string;
    /** What follows the date on a row — which anniversary it is. */
    detail?: (milestone: M) => string;
    /**
     * Rows falling today or a week from `today` carry their `badge`, and today's are tinted in `tone`
     * as well. Unset, no row stands out.
     */
    highlight?: { today: Date; tone: keyof typeof TODAY_TINT; badge: (milestone: M, when: MilestoneWhen) => ReactNode };
}

/** One kind of milestone for the month, a row per person, each linking through to their profile. */
export function MilestoneCard<M extends Milestone>({
    title,
    milestones,
    loading,
    empty,
    detail,
    highlight,
}: MilestoneCardProps<M>) {
    const openedFrom = useOpenedFromHere();
    return (
        <ListCard title={title} role="region" aria-label={title}>
            {loading
                ? Array.from({ length: SKELETON_ROWS }, (_, index) => (
                      <ListCardRow key={index}>
                          <Skeleton className="h-8 w-8 rounded-full" />
                          <Skeleton className="h-4 flex-1" />
                      </ListCardRow>
                  ))
                : milestones.length === 0
                  ? [
                        <ListCardRow key="empty">
                            <Text size="sm" muted>
                                {empty}
                            </Text>
                        </ListCardRow>,
                    ]
                  : milestones.map((milestone) => {
                        const { client, date } = milestone;
                        const when = highlight ? milestoneWhen(daysUntil(date, highlight.today)) : undefined;
                        return (
                            <ListCardRow key={client.id} className="p-0">
                                <Link
                                    to={alumnusPath(client.id)}
                                    state={openedFrom}
                                    className={cn(
                                        "flex min-w-0 flex-1 items-center gap-3 px-5 py-3",
                                        highlight && when === "today" ? TODAY_TINT[highlight.tone] : "hover:bg-muted",
                                    )}
                                >
                                    <Avatar size="sm" className="shrink-0">
                                        <AvatarGradientFallback>
                                            {initialsFor(client.firstName, client.lastName)}
                                        </AvatarGradientFallback>
                                    </Avatar>
                                    <Text as="span" size="sm" weight="medium" className="min-w-0 flex-1 truncate">
                                        {fullName(client)}
                                    </Text>
                                    {highlight && when ? highlight.badge(milestone, when) : null}
                                    <Mono as="span" size="xs" muted className="shrink-0">
                                        {[
                                            formatMilestoneDay(date),
                                            detail?.(milestone),
                                        ]
                                            .filter(Boolean)
                                            .join(" · ")}
                                    </Mono>
                                </Link>
                            </ListCardRow>
                        );
                    })}
        </ListCard>
    );
}
